"use client";
import { RootContext } from "@/contexts/rootContext";
import { db, fetchToken } from "@/lib/firebaseConfig";
import { Permission_Notification, STORAGE_KEY } from "@/utils/enum";
import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  serverTimestamp,
  setDoc
} from "firebase/firestore";
import { useCallback, useContext, useState } from "react";
import { v5 as uuidv5 } from "uuid";

const NAMESPACE = uuidv5.URL;

function hashFcmTokenUuid(token: string): string {
  return uuidv5(token, NAMESPACE);
}

export function useFCMToken() {
  const { state, dispatch } = useContext(RootContext);
  const uid = state?.user?.uid;
  const fcmToken = state.fcmToken;
  const [permission, setPermission] = useState<NotificationPermission>(
    Permission_Notification.Default
  );

  const createFcmToken = useCallback(async () => {
    const fcmToken = await fetchToken();
    dispatch({
      type: "setFcmToken",
      payload: { ...state, fcmToken }
    });

    return fcmToken;
  }, [state]);

  const saveFcmToken = useCallback(
    async (userUid: string) => {
      try {
        const token = await createFcmToken();

        if (!token || !userUid) return;

        const fcmTokenId = hashFcmTokenUuid(token);

        const memberRef = doc(db, "Members", userUid);
        const devicesCol = collection(memberRef, "Devices");
        const deviceDocRef = doc(devicesCol, fcmTokenId);

        const snapshot = await getDoc(deviceDocRef);
        if (snapshot.exists()) return;

        await setDoc(deviceDocRef, {
          token,
          createdAt: serverTimestamp()
        });
      } catch (error) {
        console.error("Error creating and saving token to Firebase:", error);
        return null;
      }
    },
    [createFcmToken]
  );

  const deleteFcmToken = useCallback(async () => {
    try {
      if (!uid || uid.trim() === "") {
        return false;
      }

      if (!fcmToken) return true;

      const fcmTokenId = hashFcmTokenUuid(fcmToken);

      const deviceRef = doc(db, "Members", uid, "Devices", fcmTokenId);

      const snapshot = await getDoc(deviceRef);
      if (!snapshot.exists()) return true;

      await deleteDoc(deviceRef);
      return true;
    } catch {
      return false;
    }
  }, [uid, fcmToken]);

  const requestNotificationPermission = useCallback(async (userUid: string) => {
    if (!("Notification" in window)) {
      console.warn("Notification API not supported.");
      return;
    }

    const current = Notification.permission;
    if (current === Permission_Notification.Granted) {
      setPermission(current);
      localStorage.setItem(STORAGE_KEY.IS_REQUEST_FIRST_TIME, "true");
      await saveFcmToken(userUid);
      return;
    }

    if (current === Permission_Notification.Default) {
      try {
        const result = await Notification.requestPermission();
        setPermission(result);
        localStorage.setItem(STORAGE_KEY.IS_REQUEST_FIRST_TIME, "true");
        if (result === Permission_Notification.Granted) {
          await saveFcmToken(userUid);
        }
      } catch (err) {
        console.error("Error requesting permission:", err);
      }
    }
  }, []);

  return {
    deleteFcmToken,
    requestNotificationPermission,
    permission,
    saveFcmToken
  };
}
